import type { ApplyJobDetails, UnfinishedInterview } from '@/api/types';

type Position = ApplyJobDetails['positions'][number];

/**
 * Prefix that namespaces a "continue this interview" choice inside the
 * position Select. Position ids are numeric strings, so a prefixed value can
 * never collide with one — which lets a single RHF field hold both kinds of
 * choice without a second piece of state.
 */
export const CONTINUE_PREFIX = 'continue:';

export interface PositionOption {
  kind: 'position';
  value: string;
  label: string;
}

export interface ContinueOption {
  kind: 'continue';
  value: string;
  label: string;
  invitationKey: string;
}

export interface PositionOptions {
  inProgress: ContinueOption[];
  available: PositionOption[];
}

export type Selection =
  | { kind: 'position'; id: string }
  | { kind: 'continue'; invitationKey: string };

/**
 * Splits the picker into the two groups the design calls for.
 *
 * A position matching an unfinished interview is dropped from `available` —
 * that removal is the whole point of the feature: with no selectable entry for
 * that role, a duplicate application is not just discouraged but unreachable.
 *
 * The match is on `position_id`, not on the role's name. The GET is
 * language-scoped (`?language=`), so comparing a localized `label` against a
 * `position_name` captured at invitation time used to break the match — and a
 * renamed role broke it too, leaving the role listed in both groups. An id
 * join has neither failure mode.
 *
 * An unfinished interview matching no open position still appears: the
 * interview exists and stays continuable even after the role closes.
 */
export function buildPositionOptions(
  positions: Position[] | undefined,
  unfinished: UnfinishedInterview[] | undefined
): PositionOptions {
  const open = positions ?? [];
  // An unfinished interview with no invitation key can't be continued (there
  // is nothing to deep-link to) and must not be offered as a choice: it would
  // render as `continue:` — a value that passes the required-field check and
  // masquerades as a normal position, POSTing a `position_id` the API rejects.
  const pending = (unfinished ?? []).filter((item) => item.invitation_key);

  const byId = new Map(open.map((position) => [position.value, position]));

  const inProgress: ContinueOption[] = pending.map((item) => ({
    kind: 'continue',
    value: `${CONTINUE_PREFIX}${item.invitation_key}`,
    // The open position's own label is authoritative: it arrives with the same
    // `?language=` as the rest of the list, so it cannot drift out of language
    // the way a `position_name` captured at invitation time can. A role that
    // has closed since has nothing to borrow a label from, so it keeps the
    // name its interview started under.
    label: byId.get(item.position_id)?.label ?? item.position_name,
    invitationKey: item.invitation_key,
  }));

  const busyIds = new Set(pending.map((item) => item.position_id));

  const available: PositionOption[] = open
    .filter((position) => !busyIds.has(position.value))
    .map((position) => ({
      kind: 'position',
      value: String(position.value),
      label: position.label,
    }));

  return { inProgress, available };
}

/** The only place a raw Select value is decoded. */
export function parseSelection(value: string): Selection | null {
  if (!value) return null;
  if (value.startsWith(CONTINUE_PREFIX)) {
    const invitationKey = value.slice(CONTINUE_PREFIX.length);
    return invitationKey ? { kind: 'continue', invitationKey } : null;
  }
  return { kind: 'position', id: value };
}
